import { ApiOperationItems } from "~enums/apiOperationItems";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import { useDeleteRosterPlayer } from "~pages/Roster/hooks/useDeleteRosterPlayer.ts";
import type { UpsertSeasonRosterModel } from "~pages/Roster/SeasonRosterListPage/types/upsertSeasonRosterModel.ts";
import type { DefaultOperationSuccessResponse } from "~types/response.ts";
import type { RosterResponse } from "~types/roster.ts";
import { isDefaultOperationSucceeded } from "~utils/defaultOperationResponse.ts";
import { useExportSeasonRoster } from "./useExportSeasonRoster.ts";
import { useImportSeasonRoster } from "./useImportSeasonRoster.tsx";
import useUpsertSeasonRosterPlayer from "./useUpsertSeasonRosterPlayer.ts";

const isRosterUpsertSucceeded = (response: RosterResponse) => Boolean(response?.data?.length);

export const useSeasonRosterService = (entityId: string) => {
  const { mutateAsync: upsertSeasonRosterPlayerFn, isPending: isUpsertPending } = useUpsertSeasonRosterPlayer();
  const { mutateAsync: deleteRosterPlayerFn, isPending: isDeletePending } = useDeleteRosterPlayer();
  const { mutateAsync: importSeasonRosterFn, isPending: isImportPending } = useImportSeasonRoster(entityId);
  const { mutateAsync: exportSeasonRosterFn, isPending: isExportPending } = useExportSeasonRoster(entityId);

  const { update, remove, executeOrThrow } = useApiOperations(ApiOperationItems.SEASON_ROSTER);

  const upsertSeasonRosterPlayer = (payload: UpsertSeasonRosterModel, successMessageId?: string) => {
    if (!successMessageId) {
      return upsertSeasonRosterPlayerFn(payload);
    }

    return update(() => upsertSeasonRosterPlayerFn(payload), {
      successMessageId,
      isSucceeded: isRosterUpsertSucceeded,
    });
  };

  const deleteSeasonRosterPlayer = async (personId: string) =>
    Boolean(
      await remove(() => deleteRosterPlayerFn(personId), {
        successMessageId: "roster.delete.success",
        isSucceeded: isDefaultOperationSucceeded,
      }),
    );

  const importSeasonRoster = async (fileName: string) =>
    executeOrThrow(() => importSeasonRosterFn(fileName), {
      successMessageId: "seasonRoster.import.started",
      isSucceeded: isDefaultOperationSucceeded,
    });

  const exportSeasonRoster = async (fieldNames: Array<string>): Promise<DefaultOperationSuccessResponse> =>
    executeOrThrow(() => exportSeasonRosterFn(fieldNames), {
      successMessageId: "seasonRoster.export.started",
      isSucceeded: isDefaultOperationSucceeded,
    });

  return {
    upsertSeasonRosterPlayer,
    isUpsertPending,
    deleteSeasonRosterPlayer,
    isDeletePending,
    importSeasonRoster,
    isImportPending,
    exportSeasonRoster,
    isExportPending,
  };
};
